import { Container, Grid, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessagesWidget } from '../components/home/MessagesWidget';
import { NextAppointmentsWidget } from '../components/home/NextAppointmentsWidget';
import { PatientQueueWidget } from '../components/home/PatientQueueWidget';
import { LoadingScreen } from '../components/LoadingScreen';
import { useApiClients } from '../hooks/useAppClients';

export default function ProfessionalHome(): JSX.Element {
  const { oystehr } = useApiClients();
  const { i18n } = useTranslation();
  const [doctorName, setDoctorName] = useState<string>('Doctor');
  const [practitionerId, setPractitionerId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoctorInfo = async (): Promise<void> => {
      try {
        if (oystehr) {
          const userInfo = await oystehr.user.me();
          // Extract name from user profile
          // For now, using a default name - will be replaced with actual user data
          const name = userInfo.name || 'Doctor';
          setDoctorName(name);

          if (userInfo.profile) {
            const id = userInfo.profile.split('/')[1];
            setPractitionerId(id);
          }
        }
      } catch (error) {
        console.error('Error fetching doctor info:', error);
        setDoctorName('Doctor');
      } finally {
        setLoading(false);
      }
    };

    void fetchDoctorInfo();
  }, [oystehr]);

  // Get current date formatted
  const getCurrentDate = (): string => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return now.toLocaleDateString(i18n.language, options);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: '#22222d',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="600" gutterBottom>
          {doctorName}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {getCurrentDate()}
        </Typography>
      </Paper>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Patient List (2/3 width) */}
        <Grid item xs={12} md={8}>
          <PatientQueueWidget practitionerId={practitionerId} />
        </Grid>

        {/* Right Column - Messages (1/3 width) */}
        <Grid item xs={12} md={4}>
          <MessagesWidget practitionerId={practitionerId} />
        </Grid>

        {/* Bottom Row - Next Appointments (Full width) */}
        <Grid item xs={12} md={6}>
          <NextAppointmentsWidget practitionerId={practitionerId} />
        </Grid>
      </Grid>
    </Container>
  );
}
